import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle } from '../../icons';
// «+ 새 발령» → 오른쪽 패널(2026-10-18 · web 레인 W10-51 · 렌즈 Karri)
import { Plus } from '../../icons';
import { SidePanel } from '../../components/shared';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import {
  listAssignments, createAssignment, applyAssignment, cancelAssignment,
  type AssignmentDto, type AssignmentKind, type CreateAssignmentInput,
} from '../../api/hr-assignment';
import { listEmployees, type EmployeeRowDto } from '../../api/hr-employee';
import { listWorkSchedules } from '../../api/hr-schedule';
import { fetchJobRoles, SCOPE_LABEL, type JobRoleDto } from '../../api/hr-job-roles';
import { listDivisions } from '../../api/organization';
import { JobGradeSelect } from '../../components/JobGradeSelect';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 인사발령 — 담당자 화면 (시안 A5, migration 130).
 *
 * ★★ **시행일이 지난 예정이 맨 앞에 선다.** 자동 시행이 없으니(믿을 만한 스케줄러가 없다)
 *    누군가 눌러야 값이 바뀐다 — 잊히면 명부가 현실과 어긋난 채로 남는다.
 * ★ 종류를 고르면 **필요한 칸만** 뜬다. 부서 이동에 직위 칸을 띄우면 안 바꿀 값을 건드리게 된다.
 * ★ 직책·겸직은 지금 담을 칸이 없어 **이력으로만** 남는다 — 화면이 그 사실을 미리 말한다
 *   (누르고 나서 "아무 일도 안 일어났다" 가 되지 않게).
 */
const KINDS: Array<{
  kind: AssignmentKind; label: string; need: 'team' | 'position' | 'role' | 'none'; note?: string;
}> = [
  { kind: 'TRANSFER', label: '부서 이동', need: 'team' },
  { kind: 'PROMOTION', label: '승진', need: 'position', note: '직위가 바뀝니다 — 직책은 그대로입니다' },
  /**
   * ★★ **승진과 보임은 다른 발령이다** (migration 144, 2026-08-29).
   *    같은 날이어도 **2건**으로 남긴다 — 한 줄에 합치면 나중에 「언제부터 팀장이었나」를
   *    직위 이력에서 역산해야 한다. 순서는 **승진 → 보임**(보임 발령문의 직위가 승진 후 값이라야
   *    앞뒤가 맞는다).
   */
  { kind: 'DUTY', label: '보임', need: 'role', note: '맡고 있던 본직은 끝나고 새 자리에 앉습니다' },
  { kind: 'CONCURRENT', label: '겸직', need: 'role', note: '지금 자리는 그대로 두고 하나 더 맡습니다' },
  { kind: 'RELIEVE', label: '보직해임', need: 'role', note: '그 직책이 끝납니다 — 직위는 그대로입니다' },
  { kind: 'LEAVE_ABSENCE', label: '휴직', need: 'none', note: '재직 상태가 «휴직» 으로 바뀝니다' },
  { kind: 'REINSTATE', label: '복직', need: 'none', note: '재직 상태가 «재직» 으로 돌아옵니다' },
  { kind: 'RESIGN', label: '퇴직', need: 'none', note: '재직 상태가 «퇴사» 로 바뀝니다' },
];

const STATUS_LABEL: Record<string, string> = { SCHEDULED: '예정', APPLIED: '시행', CANCELED: '취소' };
const STATUS_TONE: Record<string, string> = { SCHEDULED: 'is-wait', APPLIED: 'is-ok', CANCELED: '' };
// 재직 상태 이름(2026-10-18 · web 레인 W9 · 평 51 «ACTIVE → 과장») — api/hr-employee 의 EMPLOYMENT_STATUS_LABEL 과 같은 값.
// ★ 위 STATUS_LABEL 처럼 이 화면이 제 표를 든다(그 모듈을 흉내 내는 시험이 상수까지 싣지 않는다).
const EMPLOYMENT_STATUS_LABEL: Record<string, string> = { ACTIVE: '재직', LEAVE: '휴직', RESIGNED: '퇴사' };

const sideText = (s: AssignmentDto['before' | 'after']) =>
  [s.teamName, s.position, s.employmentStatus && (EMPLOYMENT_STATUS_LABEL[s.employmentStatus] ?? s.employmentStatus)]
    .filter(Boolean).join(' · ') || '—';

export function AssignmentPage() {
  const [rows, setRows] = useState<AssignmentDto[]>([]);
  const [overdue, setOverdue] = useState(0);
  const [employees, setEmployees] = useState<EmployeeRowDto[]>([]);
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  // 직책 보임 (migration 144) — 직책 목록과 부서 목록이 함께 있어야 «어느 조직의 무슨 직책» 을 고른다.
  const [roles, setRoles] = useState<JobRoleDto[]>([]);
  const [divisions, setDivisions] = useState<Array<{ id: string; name: string }>>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 새 발령 패널(W10-51) — 열 때만 그린다. 닫으면 초점은 «새 발령» 단추로 돌아간다(SidePanel · useFocusTrap).
  const [panel, setPanel] = useState(false);

  const [form, setForm] = useState<CreateAssignmentInput>({
    userId: '', kind: 'TRANSFER', effectiveDate: '', afterTeamId: '', afterPosition: '',
    afterJobRoleId: '', roleOrgId: '', reason: '',
  });
  const picked = useMemo(() => KINDS.find((k) => k.kind === form.kind)!, [form.kind]);
  /** 고른 직책 — 그 «범위» 가 어느 조직을 물을지 가른다(팀장을 부서에 보임할 수 없다). */
  const roleOf = useCallback(
    (id?: string | null) => roles.find((r) => r.id === id) ?? null,
    [roles],
  );

  const load = useCallback(async () => {
    const res = await listAssignments();
    setRows(res.assignments);
    setOverdue(res.overdue);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await load();
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
        return;
      }
      // 고르기 목록은 없어도 화면이 선다 — 따로 삼킨다.
      try {
        const emp = await listEmployees();
        if (alive) setEmployees(emp.rows.filter((e) => e.employmentStatus !== 'RESIGNED'));
      } catch { /* 사람 고르기만 못 쓴다 */ }
      try {
        const sch = await listWorkSchedules();
        if (alive) setTeams(sch.teams);
      } catch { /* 부서 고르기만 못 쓴다 */ }
      try {
        const list = await fetchJobRoles();
        if (alive) setRoles(list);
      } catch { /* 옛 서버 — 직책 고르기만 못 쓴다 */ }
      try {
        const divs = await listDivisions();
        if (alive) setDivisions(divs.map((d) => ({ id: d.id, name: d.name })));
      } catch { /* 부서 고르기만 못 쓴다 */ }
    })();
    return () => { alive = false; };
  }, [load]);

  const act = async (id: string, run: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await run();
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '처리하지 못했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const submit = async () => {
    setError(null);
    if (!form.userId || !form.effectiveDate) {
      setError('대상과 시행일을 골라 주세요.');
      return;
    }
    setBusyId('new');
    try {
      await createAssignment({
        userId: form.userId,
        kind: form.kind,
        effectiveDate: form.effectiveDate,
        afterTeamId: picked.need === 'team' ? form.afterTeamId || null : null,
        afterPosition: picked.need === 'position' ? (form.afterPosition || '').trim() || null : null,
        afterJobRoleId: picked.need === 'role' ? form.afterJobRoleId || null : null,
        roleOrgKind: picked.need === 'role' ? (roleOf(form.afterJobRoleId)?.scopeKind ?? null) : null,
        roleOrgId: picked.need === 'role' && roleOf(form.afterJobRoleId)?.scopeKind !== 'COMPANY'
          ? form.roleOrgId || null : null,
        reason: (form.reason || '').trim() || null,
      });
      setForm({ ...form, afterTeamId: '', afterPosition: '', afterJobRoleId: '', roleOrgId: '', reason: '' });
      await load();
      setPanel(false);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '등록하지 못했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="hrpage">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/assignments')}
        scope={scopeFor('/erp/hr/assignments')}
        title="인사발령"
        description="부서 이동 · 승진 · 휴직을 예정으로 세우고, 시행일에 적용합니다"
        actions={state !== 'folded' ? (
          <Button variant="primary" iconLeft={<Plus size={14} aria-hidden />} onClick={() => { setError(null); setPanel(true); }}>
            새 발령
          </Button>
        ) : undefined}
      />

      {state === 'folded' && (
        <div className="hrpage__note">인사발령을 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          {overdue > 0 && (
            <div className="hrasg__overdue" role="status">
              <AlertCircle size={16} aria-hidden />
              시행일이 지난 발령이 <b>{overdue}건</b> 있습니다 — 누르지 않으면 명부가 현실과 어긋난 채로 남습니다.
            </div>
          )}
          {error && !panel && <p className="hrcert__err" role="alert">{error}</p>}

          {panel && (
          <SidePanel
            title="새 발령"
            description="등록하면 «예정» 으로 섭니다 — 값은 시행할 때 바뀝니다"
            onClose={() => setPanel(false)}
            busy={busyId === 'new'}
          >
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          <section className="hrpage__panel hrasg__newpanel">
            <h2 className="hrpage__panelt">
              발령 등록
              <span className="hrpage__panels">등록하면 «예정» 으로 섭니다 — 값은 시행할 때 바뀝니다</span>
            </h2>
            <div className="hrcert__form">
              <label className="hrcert__field">
                <span>대상</span>
                <select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}>
                  <option value="">사람 고르기</option>
                  {employees.map((e) => (
                    <option key={e.userId} value={e.userId}>
                      {e.name} · {e.teamName ?? '회사 직속'} {e.position ?? ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="hrcert__field">
                <span>종류</span>
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as AssignmentKind })}
                >
                  {KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                </select>
              </label>
              <label className="hrcert__field">
                <span>시행일</span>
                <input
                  type="date" value={form.effectiveDate}
                  onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
                />
              </label>

              {/* 종류가 필요로 하는 칸만 뜬다 — 안 바꿀 값을 건드리지 않게. */}
              {picked.need === 'team' && (
                <label className="hrcert__field">
                  <span>바뀔 부서</span>
                  <select value={form.afterTeamId ?? ''} onChange={(e) => setForm({ ...form, afterTeamId: e.target.value })}>
                    <option value="">부서 고르기</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
              )}
              {/*
                ★ 직책은 «어느 조직» 에 대한 보임인지 함께 묻는다 — 팀장은 어느 팀의 팀장인가.
                  전사 직책(대표)은 조직을 안 묻는다.
              */}
              {picked.need === 'role' && (
                <>
                  <label className="hrcert__field">
                    <span>직책</span>
                    <select
                      value={form.afterJobRoleId ?? ''}
                      onChange={(e) => setForm({ ...form, afterJobRoleId: e.target.value, roleOrgId: '' })}
                    >
                      <option value="">직책 고르기</option>
                      {roles.map((r) => (
                        <option key={r.id} value={r.id}>{r.name} · {SCOPE_LABEL[r.scopeKind]}</option>
                      ))}
                    </select>
                  </label>
                  {roleOf(form.afterJobRoleId)?.scopeKind === 'TEAM' && (
                    <label className="hrcert__field">
                      <span>어느 팀</span>
                      <select value={form.roleOrgId ?? ''} onChange={(e) => setForm({ ...form, roleOrgId: e.target.value })}>
                        <option value="">팀 고르기</option>
                        {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </label>
                  )}
                  {roleOf(form.afterJobRoleId)?.scopeKind === 'DIVISION' && (
                    <label className="hrcert__field">
                      <span>어느 부서</span>
                      <select value={form.roleOrgId ?? ''} onChange={(e) => setForm({ ...form, roleOrgId: e.target.value })}>
                        <option value="">부서 고르기</option>
                        {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </label>
                  )}
                </>
              )}
              {picked.need === 'position' && (
                <label className="hrcert__field">
                  <span>바뀔 직위</span>
                  {/* 승진이 마스터 밖 이름으로 나가면 그 사람만 목록 밖 직위가 된다(판 2). */}
                  <JobGradeSelect
                    value={form.afterPosition ?? ''} manage placeholder="고르세요"
                    onChange={(v) => setForm({ ...form, afterPosition: v })}
                  />
                </label>
              )}

              <label className="hrcert__field">
                <span>사유</span>
                <input
                  value={form.reason ?? ''} maxLength={300} placeholder="예) 브랜드 마케팅 강화에 따른 인력 재배치"
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                />
              </label>
              <Button variant="primary" onClick={submit} disabled={busyId === 'new'}>
                {busyId === 'new' ? '등록 중…' : '등록하기'}
              </Button>
            </div>
            {picked.note && <p className="hrasg__note">{picked.note}</p>}
          </section>
          </SidePanel>
          )}

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              발령 이력
              <span className="hrpage__panels">{rows.length}건</span>
            </h2>
            {rows.length === 0 ? (
              <p className="hrpage__empty">아직 등록된 발령이 없습니다.</p>
            ) : (
              <table className="hrpage__tbl">
                <thead>
                  <tr>
                    <th>발령번호</th><th>시행일</th><th>종류</th><th>대상</th>
                    <th>변경 전 → 후</th><th className="hrpage__act">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={r.overdue ? 'is-overdue' : undefined}>
                      <td>{r.docNo ?? '—'}</td>
                      <td>
                        {r.effectiveDate}
                        {r.overdue ? <span className="hrasg__flag">지남</span> : null}
                      </td>
                      <td>
                        {r.kindLabel}
                        {/* ★ 「이력만」 표시는 없앴다 — 보임이 실제로 일어나게 된 뒤로 늘 거짓이다. */}
                      </td>
                      <td><b>{r.userName ?? '—'}</b></td>
                      <td>
                        <span className="hrpage__dim">{sideText(r.before)}</span>
                        {' → '}
                        {sideText(r.after)}
                        {r.reason ? <span className="hrpage__dim"> · {r.reason}</span> : null}
                      </td>
                      <td className="hrpage__act">
                        {r.status === 'SCHEDULED' ? (
                          <>
                            <Button variant="primary" size="compact" disabled={busyId === r.id} onClick={() => act(r.id, () => applyAssignment(r.id))}
                            >시행</Button>
                            <Button variant="secondary" size="sm" disabled={busyId === r.id} onClick={() => act(r.id, () => cancelAssignment(r.id))}
                            >취소</Button>
                          </>
                        ) : (
                          <span className={`hrpage__pill ${STATUS_TONE[r.status] ?? ''}`}>
                            {STATUS_LABEL[r.status] ?? r.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
